import { onCleanup } from "solid-js";

import { FetchError } from "~/request";

import type { Markup, MarkupChange, Stroke } from "./markup";
import type { SlideStrokes } from "./types";

// Long enough that an eraser drag saves once, short enough that the tablet sees a line about when it is finished.
const SAVE_AFTER_MS = 300;
const RETRY_AFTER_MS = 3000;

type Pending = Map<string, { add: Map<string, Stroke>; remove: Set<string> }>;

/** Saves this screen's own drawing steps onto the version's layer, a moment after each one, folded together per
 *  slide. `post` sends them, or answers null when no saved version is on show to draw on, and the steps stay on the
 *  screen alone. A save that does not get through keeps its steps for the next one, under anything drawn since; one
 *  refused drops them. pagehide sends what is waiting. */
export function createMarkupSaver(
  markup: Markup,
  post: (changes: MarkupChange[], keepalive: boolean) => Promise<unknown> | null,
): void {
  let pending: Pending = new Map();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const fold = (into: Pending, change: MarkupChange) => {
    const slide = into.get(change.slide) ?? { add: new Map(), remove: new Set<string>() };
    for (const id of change.remove) {
      slide.add.delete(id);
      slide.remove.add(id);
    }
    for (const stroke of change.add) {
      slide.remove.delete(stroke.id);
      slide.add.set(stroke.id, stroke);
    }
    into.set(change.slide, slide);
  };
  const changesOf = (from: Pending): MarkupChange[] =>
    [...from].map(([slide, { add, remove }]) => ({
      slide,
      add: [...add.values()],
      remove: [...remove],
    }));

  const flush = (keepalive = false) => {
    clearTimeout(timer);
    if (pending.size === 0) return;
    const sending = pending;
    pending = new Map();
    const sent = post(changesOf(sending), keepalive);
    if (!sent) return;
    sent.catch((error: unknown) => {
      // Refused (the show's version gone, a slide it lacks): sending it again would be refused again.
      if (error instanceof FetchError && error.status < 500 && error.status !== 429) return;
      const newer = pending;
      pending = sending;
      changesOf(newer).forEach((change) => fold(pending, change));
      clearTimeout(timer);
      timer = setTimeout(() => flush(), RETRY_AFTER_MS);
    });
  };

  markup.onOwnChange((change) => {
    fold(pending, change);
    clearTimeout(timer);
    timer = setTimeout(() => flush(), SAVE_AFTER_MS);
  });
  const onPageHide = () => flush(true);
  window.addEventListener("pagehide", onPageHide);
  onCleanup(() => {
    window.removeEventListener("pagehide", onPageHide);
    flush(true);
  });
}

/** A screen on another device takes the others' lines from the server, through the live read it already makes every
 *  second: it says which drawing it holds (`seen`), and when the answer carries a newer one, merges what changed since
 *  into its own (`take`), so its own lines still on their way are kept. The drawing is `<layer id>.<revision>`; an
 *  answer older than the one taken is dropped. */
export function createLiveDrawings(markup: Markup): {
  seen: () => string | null;
  take: (drawn: string | null, drawings: SlideStrokes[]) => void;
} {
  let held: string | null = null;
  let sheet: SlideStrokes[] = [];
  const parse = (drawn: string | null) => {
    const at = drawn?.lastIndexOf(".") ?? -1;
    return drawn && at > 0
      ? { layer: drawn.slice(0, at), revision: Number(drawn.slice(at + 1)) }
      : null;
  };
  return {
    seen: () => held,
    take: (drawn, drawings) => {
      const was = parse(held);
      const now = parse(drawn);
      if (was && now && was.layer === now.layer && now.revision <= was.revision) return;
      const strokesOf = (from: SlideStrokes[], slide: string) =>
        from.find((entry) => entry.slide === slide)?.strokes ?? [];
      const slides = new Set([...sheet, ...drawings].map((entry) => entry.slide));
      markup.merge(
        [...slides].map((slide) => {
          const before = strokesOf(sheet, slide);
          const after = strokesOf(drawings, slide);
          return {
            slide,
            add: after.filter((stroke) => !before.some((other) => other.id === stroke.id)),
            remove: before
              .filter((stroke) => !after.some((other) => other.id === stroke.id))
              .map((stroke) => stroke.id),
          };
        }),
      );
      held = drawn;
      sheet = drawings;
    },
  };
}
